import type {
	PlatformFieldDefinitionOption,
	PlatformFieldDefinitionQuery,
	PlatformFieldDefinitionResult,
	PlatformFieldsGateway,
} from '@/platform/ui/contracts'
import { ApiService } from './api'

export type FieldValueType = 'string' | 'text' | 'integer' | 'decimal' | 'bool' | 'datetime' | 'entity_ref' | 'secret_ref' | 'json'
export type FieldContextPolicy = 'none' | 'optional' | 'required'
export type FieldNamespaceKind = 'space' | 'module'

export interface FieldPermissionsDto {
	can_view: boolean
	can_edit_definition: boolean
	can_edit_values: boolean
	can_delete: boolean
}

export interface FieldDefinitionDto {
	id: number
	space_id: number
	target_entity_type: string
	namespace_key: string
	field_key: string
	display_name: string
	value_type_key: FieldValueType
	context_policy_key: FieldContextPolicy
	searchable: boolean
	created_at: string
	updated_at: string
	namespace_kind: FieldNamespaceKind
	permissions: FieldPermissionsDto
}

export interface FieldsPaginationDto {
	page: number
	page_size: 20 | 50 | 100
	total: number
	pages: number
}

export interface FieldDefinitionListDto {
	items: FieldDefinitionDto[]
	pagination: FieldsPaginationDto
	filter_values: {
		namespace_kind: FieldNamespaceKind[]
		value_type_key: FieldValueType[]
		context_policy_key: FieldContextPolicy[]
		searchable: boolean[]
	}
}

export interface FieldDefinitionListQuery {
	page?: number
	page_size?: 20 | 50 | 100
	search?: string
	target_entity_type?: string
	namespace_kind?: FieldNamespaceKind | ''
	namespace_key?: string
	value_type_key?: FieldValueType | ''
	value_type_keys?: string
	context_policy_key?: FieldContextPolicy | ''
	searchable?: boolean | null
	sort?: 'updated_at' | 'created_at' | 'display_name' | 'field_key' | 'id'
	direction?: 'asc' | 'desc'
}

export interface FieldEntityTypeDto {
	entity_type: string
	title: string
	available: boolean
	reference_mode: 'provider' | 'technical_id'
}

export interface FieldEntityReferenceDto {
	entity_type: string
	entity_id: string
	title: string
	subtitle?: string | null
	status_key?: string | null
}

export interface FieldEntityReferenceListDto {
	items: FieldEntityReferenceDto[]
	pagination: FieldsPaginationDto
}

export interface FieldValueDto {
	id: number
	space_id: number
	definition_id: number
	target_entity_type: string
	target_entity_id: string
	target_entity?: FieldEntityReferenceDto | null
	context_key: string
	value_type_key: FieldValueType
	value?: unknown
	has_value?: boolean
	created_at: string
	updated_at: string
}

export interface FieldValueListDto {
	definition: FieldDefinitionDto
	items: FieldValueDto[]
	pagination: FieldsPaginationDto
}

export interface FieldValueListQuery {
	page?: number
	page_size?: 20 | 50 | 100
	target_entity_id?: string
	context_key?: string
	search_value?: string | number | boolean
	search_entity_type?: string
	search_entity_id?: string
	sort?: 'updated_at' | 'created_at' | 'target_entity_id' | 'id'
	direction?: 'asc' | 'desc'
}

export interface FieldDefinitionCreateDto {
	target_entity_type: string
	field_key: string
	display_name: string
	value_type_key: FieldValueType
	context_policy_key: FieldContextPolicy
	searchable: boolean
}

export interface FieldDefinitionUpdateDto {
	display_name: string
	searchable: boolean
	updated_at: string
}

export interface FieldValueAddressDto {
	definition_id: number
	target_entity_id: string
	context_key?: string
}

export interface FieldValueWriteDto extends FieldValueAddressDto {
	value: unknown
}

export interface FieldsApiProblem {
	code: string
	message: string
	status: number
	path?: string
}

export class FieldsApiError extends Error {
	readonly code: string
	readonly status: number
	readonly path?: string

	constructor(problem: FieldsApiProblem) {
		super(problem.message)
		this.name = 'FieldsApiError'
		this.code = problem.code
		this.status = problem.status
		this.path = problem.path
	}
}

export function normalizeFieldsApiError(error: unknown): FieldsApiProblem {
	if (error instanceof FieldsApiError) {
		let message = error.message || 'Не удалось выполнить запрос Fields.'
		if (error.code === 'fields_concurrent_update') message = 'Поле уже изменилось. Перезагрузите данные и повторите действие.'
		if (error.code === 'fields_read_only_namespace') message = 'Это поле принадлежит модулю и доступно только для просмотра.'
		if (error.status === 403 && error.code === 'fields_request_failed') message = 'Недостаточно прав для этого действия.'
		return { code: error.code, message, status: error.status, path: error.path }
	}
	if (error instanceof Error) return { code: 'fields_request_failed', message: error.message, status: 0 }
	return { code: 'fields_request_failed', message: 'Не удалось выполнить запрос Fields.', status: 0 }
}

export class FieldsService {
	private readonly cpDomain: string

	constructor(private readonly apiService: ApiService) {
		if (!apiService) throw new Error('ApiService не инициализирован')
		this.cpDomain = apiService.getConfig().cp_domain
	}

	listDefinitions(spaceId: string | number, query: FieldDefinitionListQuery = {}): Promise<FieldDefinitionListDto> {
		return this.request(spaceId, `/definitions${this.query(query)}`, 'GET')
	}

	getDefinition(spaceId: string | number, definitionId: number): Promise<FieldDefinitionDto> {
		return this.request(spaceId, `/definition/${definitionId}`, 'GET')
	}

	createDefinition(spaceId: string | number, input: FieldDefinitionCreateDto): Promise<FieldDefinitionDto> {
		return this.request(spaceId, '/definition', 'POST', input)
	}

	updateDefinition(spaceId: string | number, definitionId: number, input: FieldDefinitionUpdateDto): Promise<FieldDefinitionDto> {
		return this.request(spaceId, `/definition/${definitionId}`, 'PATCH', input)
	}

	listValues(spaceId: string | number, definitionId: number, query: FieldValueListQuery = {}): Promise<FieldValueListDto> {
		return this.request(spaceId, `/values${this.query({ definition_id: definitionId, ...query })}`, 'GET')
	}

	getValue(spaceId: string | number, address: FieldValueAddressDto): Promise<{ definition: FieldDefinitionDto; value: FieldValueDto }> {
		return this.request(spaceId, `/value${this.query(address)}`, 'GET')
	}

	putValue(spaceId: string | number, input: FieldValueWriteDto): Promise<FieldValueDto> {
		return this.request(spaceId, '/value', 'PUT', input)
	}

	clearValue(spaceId: string | number, address: FieldValueAddressDto): Promise<{ cleared: boolean }> {
		return this.request(spaceId, '/value', 'DELETE', address)
	}

	listEntityTypes(spaceId: string | number): Promise<FieldEntityTypeDto[]> {
		return this.request(spaceId, '/entity-types', 'GET')
	}

	listEntityReferences(spaceId: string | number, entityType: string, search = '', page = 1): Promise<FieldEntityReferenceListDto> {
		return this.request(spaceId, `/entity-references/${encodeURIComponent(entityType)}${this.query({ search, page, page_size: 20 })}`, 'GET')
	}

	selectorGateway(): PlatformFieldsGateway {
		return {
			listDefinitions: async query => this.selectorList(query),
			getDefinition: async (spaceId, definitionId) => mapDefinitionOption(await this.getDefinition(spaceId, definitionId)),
		}
	}

	private async selectorList(query: PlatformFieldDefinitionQuery): Promise<PlatformFieldDefinitionResult> {
		const result = await this.listDefinitions(query.spaceId, {
			page: query.page,
			page_size: query.pageSize,
			search: query.search,
			target_entity_type: query.targetEntityType,
			namespace_kind: query.namespaceKinds?.length === 1 ? query.namespaceKinds[0] : '',
			value_type_keys: query.allowedValueTypes?.join(','),
		})
		return { items: result.items.map(mapDefinitionOption), pagination: { ...result.pagination } }
	}

	private async request<T>(spaceId: string | number, path: string, method: string, body?: unknown): Promise<T> {
		try {
			const response = await this.apiService.doFetch(this.cpDomain, `/${spaceId}/fields/s${path}`, method, body)
			const result = await this.apiService.getResponseJson(response)
			if (!result.success) {
				const data = result.data as { problem?: { code?: string; message?: string; path?: string } } | undefined
				const problem = data?.problem
				throw new FieldsApiError({
					code: problem?.code || 'fields_request_failed',
					message: problem?.message || String(result.error || 'Ошибка Fields API'),
					status: result.response_code ?? response.status,
					path: problem?.path,
				})
			}
			return result.data as T
		} catch (error) {
			if (error instanceof FieldsApiError) throw error
			throw new FieldsApiError(normalizeFieldsApiError(error))
		}
	}

	private query(values: object): string {
		const params = new URLSearchParams()
		for (const [key, value] of Object.entries(values)) {
			if (value === undefined || value === null || value === '') continue
			params.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value))
		}
		const query = params.toString()
		return query ? `?${query}` : ''
	}
}

function mapDefinitionOption(definition: FieldDefinitionDto): PlatformFieldDefinitionOption {
	return {
		id: definition.id,
		targetEntityType: definition.target_entity_type,
		namespaceKey: definition.namespace_key,
		namespaceKind: definition.namespace_kind,
		fieldKey: definition.field_key,
		displayName: definition.display_name,
		valueTypeKey: definition.value_type_key,
		contextPolicyKey: definition.context_policy_key,
		searchable: definition.searchable,
		editable: definition.permissions.can_edit_definition,
	}
}
